import { FC, ReactElement, useEffect, useState } from 'react';
import { Network, SocialAccount } from 'api';
import { useNotify } from 'hooks';
import { LOCAL_STORAGE_KEY_ACCESS_KEY, CONNECTION_SUCCESS_MESSAGE } from 'app-constants';
import { ListPublicSocialConnectionsQuery, useListPublicSocialConnectionsQuery } from 'api/public';
import { useQueryClient } from 'react-query';
import { usePublicPostContext } from '../PublicPostContext';
import { popup } from 'utilities';

type IProps = {
  network: Network;
  children: (
    socialAccount: SocialAccount,
    connect: (callback: (socialAccount: SocialAccount) => void) => void
  ) => ReactElement;
};

const PublicSocialConnection: FC<IProps> = ({ network, children }) => {
  const notify = useNotify();
  const { data } = useListPublicSocialConnectionsQuery(getSocialConnectionsVariables(), {
    onError: notify.queryError,
    onSuccess: (data: ListPublicSocialConnectionsQuery) => {
      const { access_key } = data.listPublicSocialConnections;
      localStorage.setItem(LOCAL_STORAGE_KEY_ACCESS_KEY, access_key);
    },
  });
  const socialAccount = getPublicSocialAccount(data);
  const [connectCallback, setConnectCallback] = useState(null);
  useEffect(() => {
    window.addEventListener('message', event => void onMessage(event), false);
    return () => {
      window.removeEventListener('message', event => void onMessage(event), false);
    };
  }, [connectCallback]);
  const queryClient = useQueryClient();
  const { content } = usePublicPostContext().state;

  function onMessage(event: MessageEvent) {
    if (!event.data) {
      return;
    }
    if (event.data?.message === CONNECTION_SUCCESS_MESSAGE) {
      const access_key = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_KEY);
      queryClient.fetchQuery(useListPublicSocialConnectionsQuery.getKey({ access_key })).then(
        (data: ListPublicSocialConnectionsQuery) => {
          if (connectCallback) {
            connectCallback.callback(getPublicSocialAccount(data));
            setConnectCallback(null);
          }
        },
        error => {
          console.log('Error', error);
        }
      );
    }
  }

  function getSocialConnectionsVariables() {
    const accessKey = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_KEY);
    return accessKey !== 'null' ? { access_key: accessKey } : {};
  }

  function getPublicSocialAccount(query: ListPublicSocialConnectionsQuery) {
    const items = query?.listPublicSocialConnections?.items || [];
    return items.find(item => item.provider === network);
  }

  function handleConnect(connectCallback: (socialAccount: SocialAccount) => void) {
    setConnectCallback({ callback: connectCallback });
    popup(`connect/${network}/${content.content_id}`, 'Connection Popup', 650, 650);
  }

  return children(socialAccount, handleConnect);
};

export default PublicSocialConnection;
